import { PASSO_DA_GRADE_MINUTOS } from '@arena/shared/schemas';
import {
  horaDe,
  horaNoRelogio,
  MINUTOS_DO_DIA,
  minutosDe,
  minutosNaGrade,
} from '@arena/shared/tempo';

/**
 * A geometria da grade de `#v-agenda`. Funções puras.
 *
 * O protótipo desenha uma linha por hora, com um card por célula. Isso
 * não comporta uma locação de 1h30, que o próprio `#m-reserva` oferece.
 * Aqui a grade é um CSS grid de meia em meia hora — cada hora continua
 * com os 54px do protótipo, em duas trilhas de 27px — e o card ocupa as
 * trilhas do intervalo REAL dele.
 *
 * A grade de um dia pode seguir pela madrugada: a arena que fecha às
 * 02:00 guarda o fechamento como "26:00", e a grade de sábado vai até as
 * 02:00 de domingo. Por isso tudo aqui mede em minutos DA GRADE, contados
 * da meia-noite que abre o dia — a 01:00 seguinte é 1500, e a hora
 * "25:00". Essa hora estendida é da tela: antes de ir para a API ela
 * vira calendário (`noCalendario`), e para a pessoa ela se lê "01:00"
 * (`horaNoRelogio`).
 */

/** Uma trilha da grade: meia hora. A hora do protótipo tem 54px. */
export const ALTURA_DA_TRILHA = 27;

export interface Janela {
  /** Minutos do dia, já alinhados ao passo da grade. */
  readonly abertura: number;
  readonly fechamento: number;
}

export function janelaDaGrade(horario: { inicio: string; fim: string }): Janela {
  const passo = PASSO_DA_GRADE_MINUTOS;
  return {
    abertura: Math.floor(minutosDe(horario.inicio) / passo) * passo,
    fechamento: Math.ceil(minutosDe(horario.fim) / passo) * passo,
  };
}

export function trilhasDaJanela(janela: Janela): number {
  return (janela.fechamento - janela.abertura) / PASSO_DA_GRADE_MINUTOS;
}

/** Um rótulo de hora na coluna da esquerda, e as trilhas que ele cobre. */
export interface FaixaDeHora {
  readonly rotulo: string;
  readonly trilhaInicial: number;
  readonly trilhas: number;
  readonly minuto: number;
  /** Depois da meia-noite: a madrugada do dia seguinte, ainda na grade deste dia. */
  readonly madrugada: boolean;
}

/**
 * As faixas de hora: "07:00" cobrindo duas trilhas, "08:00"… Se a arena
 * abre às 07:30, a primeira faixa cobre só uma.
 */
export function faixasDeHora(janela: Janela): FaixaDeHora[] {
  const faixas: FaixaDeHora[] = [];
  const passo = PASSO_DA_GRADE_MINUTOS;
  let minuto = janela.abertura;

  while (minuto < janela.fechamento) {
    const proximaHora = Math.min((Math.floor(minuto / 60) + 1) * 60, janela.fechamento);
    faixas.push({
      rotulo: horaNoRelogio(horaDe(minuto)),
      trilhaInicial: (minuto - janela.abertura) / passo,
      trilhas: (proximaHora - minuto) / passo,
      minuto,
      madrugada: minuto >= MINUTOS_DO_DIA,
    });
    minuto = proximaHora;
  }
  return faixas;
}

export interface Posicao {
  /** Índice da primeira trilha, a partir de 0. */
  readonly trilhaInicial: number;
  readonly trilhas: number;
  readonly minutoInicial: number;
  readonly minutoFinal: number;
}

/**
 * Onde um intervalo cai na grade do dia `dia`, no fuso da arena.
 *
 * Em minutos da grade: a marcação da 01:00 seguinte começa em 1500 e cai
 * na madrugada, se a janela chega lá. A que começa na véspera é recortada
 * na abertura, a que passa do fechamento é recortada nele, e a que fica
 * inteira fora da janela de funcionamento não aparece (`null`).
 */
export function posicaoNaGrade(
  intervalo: { inicio: string; fim: string },
  dia: string,
  fuso: string,
  janela: Janela,
): Posicao | null {
  const de = minutosNaGrade(intervalo.inicio, dia, fuso);
  const ate = minutosNaGrade(intervalo.fim, dia, fuso);

  const a = Math.max(de, janela.abertura);
  const b = Math.min(ate, janela.fechamento);
  if (b <= a) return null;

  const passo = PASSO_DA_GRADE_MINUTOS;
  const trilhaInicial = Math.floor((a - janela.abertura) / passo);
  const trilhaFinal = Math.ceil((b - janela.abertura) / passo);
  return {
    trilhaInicial,
    trilhas: Math.max(1, trilhaFinal - trilhaInicial),
    minutoInicial: a,
    minutoFinal: b,
  };
}

export interface BlocoLivre {
  readonly quadraId: string;
  readonly trilhaInicial: number;
  readonly trilhas: number;
  /** A hora DA GRADE do início ("25:00" na madrugada) — o que abre `#m-reserva` já preenchido. */
  readonly hora: string;
}

/**
 * Os "livre" tracejados do protótipo: as trilhas sem marcação de cada
 * quadra, agrupadas em blocos que não atravessam a virada da hora — a
 * grade continua parecendo a do protótipo, uma célula por hora.
 */
export function blocosLivres(
  quadraIds: readonly string[],
  ocupadas: readonly { quadraId: string; posicao: Posicao }[],
  janela: Janela,
): BlocoLivre[] {
  const total = trilhasDaJanela(janela);
  const passo = PASSO_DA_GRADE_MINUTOS;
  const blocos: BlocoLivre[] = [];

  for (const quadraId of quadraIds) {
    const ocupada = new Array<boolean>(total).fill(false);
    for (const o of ocupadas) {
      if (o.quadraId !== quadraId) continue;
      for (
        let t = o.posicao.trilhaInicial;
        t < o.posicao.trilhaInicial + o.posicao.trilhas;
        t += 1
      ) {
        if (t >= 0 && t < total) ocupada[t] = true;
      }
    }

    let inicio: number | null = null;
    const fechar = (fim: number) => {
      if (inicio === null) return;
      blocos.push({
        quadraId,
        trilhaInicial: inicio,
        trilhas: fim - inicio,
        hora: horaDe(janela.abertura + inicio * passo),
      });
      inicio = null;
    };

    for (let t = 0; t < total; t += 1) {
      const minuto = janela.abertura + t * passo;
      if (minuto % 60 === 0) fechar(t);
      if (ocupada[t]) fechar(t);
      else if (inicio === null) inicio = t;
    }
    fechar(total);
  }

  return blocos;
}

/** A hora da grade de uma trilha — para o soltar do arrastar. */
export function horaDaTrilha(trilha: number, janela: Janela): string {
  return horaDe(janela.abertura + trilha * PASSO_DA_GRADE_MINUTOS);
}

// ---------------------------------------------------------------------
// Os horários de começo — o seletor de `#m-reserva` e do remarcar.
// ---------------------------------------------------------------------

/** Um trecho ocupado de uma quadra, em minutos da grade. */
export interface TrechoOcupado {
  readonly de: number;
  readonly ate: number;
}

/**
 * Os trechos ocupados de uma quadra na grade de `dia`. A marcação que se
 * está remarcando sai da conta (`ignorar`): ela não colide consigo mesma.
 */
export function trechosOcupados(
  marcacoes: readonly {
    id: string;
    quadraId: string;
    inicio: string;
    fim: string;
    canceladaEm?: string | null;
  }[],
  quadraId: string,
  dia: string,
  fuso: string,
  ignorar?: string,
): TrechoOcupado[] {
  return marcacoes
    .filter((m) => m.quadraId === quadraId && !m.canceladaEm && m.id !== ignorar)
    .map((m) => ({
      de: minutosNaGrade(m.inicio, dia, fuso),
      ate: minutosNaGrade(m.fim, dia, fuso),
    }));
}

export type SituacaoDoHorario = 'livre' | 'ocupado' | 'nao-cabe';

/**
 * Um começo possível, com a duração escolhida: cabe antes de fechar, e não
 * encosta em nada da quadra. É a mesma conta do servidor — que confere de
 * novo ao salvar, porque a grade na tela pode estar velha.
 */
export function situacaoDoHorario(
  inicio: number,
  duracaoMinutos: number,
  janela: Janela,
  ocupados: readonly TrechoOcupado[],
): SituacaoDoHorario {
  const fim = inicio + duracaoMinutos;
  if (inicio < janela.abertura || fim > janela.fechamento) return 'nao-cabe';
  return ocupados.some((o) => o.de < fim && o.ate > inicio) ? 'ocupado' : 'livre';
}
